<template>
  <article :class="classes" :aria-label="meta.label">
    <header :class="$style.head">
      <div :class="$style.titleRow">
        <h3 :class="$style.title">{{ meta.label }}</h3>
        <p
          v-if="summary && uiState === 'finished'"
          :class="[$style.summary, { [$style.summaryEmpty]: isSummaryEmpty }]"
        >
          <ui-icon
            name="xp"
            :class="[
              $style.summaryIcon,
              { [$style.summaryIconEmpty]: isSummaryEmpty }
            ]"
          />
          <span :class="$style.points">
            {{ `${isSummaryEmpty ? '' : '+'}` }} {{ formattedPoints }}</span
          >
        </p>
        <span v-if="showLockBadge" :class="$style.lockBadge">
          <ui-icon
            name="lock-circled"
            :class="$style.lockIcon"
            aria-hidden="true"
          />
        </span>
      </div>
      <p :class="$style.hint">
        {{ hintParts.before }}
        <span :class="$style.hintHighlight">{{ hintParts.highlight }}</span>
      </p>
    </header>

    <div :class="$style.slots">
      <category-slot
        v-for="(slot, index) in slots"
        :key="`${category.id}-${index}`"
        :team="slot.team"
        :is-correct="slot.isCorrect"
        :locked="locked"
        :in-flight="slot.inFlight"
        :disabled="disabled"
        :compact="compactMode"
        @click="emit('slot-click', { index, team: slot.team })"
        @remove="slot.team && emit('remove-team', slot.team.id)"
        @drop="onDrop"
      />
    </div>
  </article>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import UiIcon from '@/ui-core/components/icon/index.vue'
import CategorySlot from './CategorySlot.vue'
import { usePickemCounter } from '../../../composables/use-pickem-counter'
import { useSwissCategoryMeta } from '../../../composables/use-swiss-category-meta'
import { swissCategoryHintParts } from '../../../utils/swiss-category-label'
import type {
  PickemTeam,
  StageUiState,
  SwissCategory,
  SwissPrediction,
  SwissResult
} from '../../../types'

defineOptions({ name: 'PickemSwissCategoryCard' })

interface SlotSummary {
  team: PickemTeam | null
  isCorrect: boolean | null
  inFlight: boolean
}

interface CategorySummary {
  points: number
  correct: number
  total: number
}

const props = withDefaults(
  defineProps<{
    category: SwissCategory
    teamsById: Record<number, PickemTeam>
    predictions: SwissPrediction[]
    results: SwissResult[] | null
    // ID команд, которые сейчас «летят» к серверу — слот блокируется.
    inFlightTeamIds?: ReadonlySet<number>
    locked?: boolean
    disabled?: boolean
    compactMode?: boolean
    // true → визуальный вид «Результат» (вторая вкладка тогглера).
    resultsView?: boolean
    summary?: CategorySummary | null
    uiState?: StageUiState | null
  }>(),
  {
    inFlightTeamIds: () => new Set<number>(),
    locked: false,
    disabled: false,
    compactMode: false,
    resultsView: false,
    summary: null,
    uiState: null
  }
)

const emit = defineEmits<{
  'slot-click': [payload: { index: number; team: PickemTeam | null }]
  'remove-team': [teamId: number]
  'drop-team': [payload: { teamId: number; categoryId: number }]
}>()

const cssModule = useCssModule()

const { meta } = useSwissCategoryMeta(() => props.category, {
  compactMode: () => props.compactMode
})

const hintParts = computed(() =>
  swissCategoryHintParts(props.category, props.resultsView)
)

const { formatted: formattedPoints } = usePickemCounter(
  () => props.summary?.points
)

const isSummaryEmpty = computed(() => (props.summary?.points || 0) === 0)

const showLockBadge = computed(() => props.locked && !props.resultsView)

const classes = computed(() => [
  cssModule.card,
  cssModule[`color-${meta.value.color}`],
  props.compactMode && cssModule.compact,
  props.disabled && !props.resultsView && cssModule.disabled
])

// Раскладываем pred в слоты по slots_count. Если slots_count==0 (open-ended) —
// показываем заполненные + одну placeholder-ячейку для drop-таргета.
const slots = computed<SlotSummary[]>(() => {
  // Если смотрим прогноз юзера
  if (!props.resultsView) {
    const filtered = props.predictions.filter(
      p => p.category_id === props.category.id
    )
    const teams: SlotSummary[] = filtered.map(p => ({
      team: props.teamsById[p.team_id] ?? null,
      isCorrect: p.is_correct,
      inFlight: props.inFlightTeamIds.has(p.team_id)
    }))

    const capacity = props.category.slots_count
    if (!capacity) {
      if (!props.resultsView && !props.locked) {
        teams.push({ team: null, isCorrect: null, inFlight: false })
      }
      return teams
    }

    const padded: SlotSummary[] = [...teams]
    while (padded.length < capacity) {
      padded.push({ team: null, isCorrect: null, inFlight: false })
    }
    return padded.slice(0, capacity)
  }

  // Если выбран просмотр результата
  if (!props.results) {
    return []
  }

  return props.results
    .filter(resultTeam => resultTeam.category_id === props.category.id)
    .map(resultTeam => {
      return {
        team: props.teamsById[resultTeam.team_id],
        inFlight: false,
        isCorrect: null
      }
    })
})

const onDrop = (teamId: number) => {
  emit('drop-team', { teamId, categoryId: props.category.id })
}
</script>

<style lang="scss" module>
.card {
  position: relative;
  overflow: hidden;
  container-type: inline-size;
  padding: 2em;
  border-radius: 1.2em;
  background-color: color-get(--root-rgb-bg-card);
  display: flex;
  flex-direction: column;
  gap: 2.4em;

  @include down(md) {
    padding: 1.6em;
  }

  @include down(sm) {
    justify-content: space-between;
  }

  @container (min-width: 32em) {
    padding: 2em;

    .slots {
      gap: 1.2em;
    }
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }
}

@mixin category-glow($end-color) {
  &::before {
    background: linear-gradient(
      158.85deg,
      color-get(--root-rgb-bg-card) 0%,
      color-get(--root-rgb-bg-card) 45.86%,
      $end-color 100%
    );
  }
}

.color-success {
  @include category-glow(color-get(--root-rgb-accent-2, 0.45));

  .title {
    color: color-get(--root-rgb-system-success);
  }
}

.color-danger {
  @include category-glow(rgba(239, 16, 0, 0.45));

  .title {
    color: color-get(--root-rgb-system-error);
  }
}

.color-neutral {
  border-color: color-get(--root-rgb-bg-border);
}

.head {
  display: flex;
  flex-direction: column;
  gap: 0.8em;
}

.titleRow {
  display: flex;
  align-items: flex-start;
  gap: 0.4em;
  flex-wrap: wrap;

  @include down(md) {
    justify-content: space-between;
    align-items: center;
  }
}

.title {
  margin: 0;
  @include typo-get(headline-l);
  color: color-get(--root-rgb-text-1);

  @include down(md) {
    @include typo-get(headline-s);
  }
}

.hint {
  margin: 0;
  @include typo-get(body-m);
  color: color-get(--root-rgb-text-2);

  @include down(md) {
    @include typo-get(body-xs);
  }
}

.hintHighlight {
  color: color-get(--root-rgb-text-1);
}

.summary {
  margin: 0 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
  padding: 0.3em 1em 0.3em 0.8em;
  border-radius: 0.4em;
  background-color: color-get(--root-rgb-bg-tone-light);
  @include typo-get(subhead-s);
  color: color-get(--root-rgb-accent-2);
  white-space: nowrap;

  @include down(md) {
    margin: 0;
  }
}

.summaryEmpty {
  color: color-get(--root-rgb-text-2);
}

.summaryIcon {
  width: 1em;
  flex-shrink: 0;
  color: color-get(--root-rgb-accent-2);
}

.summaryIconEmpty {
  color: color-get(--root-rgb-text-2);
}

.points {
  font-variant-numeric: tabular-nums;
}

.slots {
  display: flex;
  flex-wrap: wrap;
  gap: 0.8em;

  @include down(md) {
    justify-content: center;
  }
}

.compact {
  padding: 1.6em;
  gap: 1.6em;
}

.lockBadge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2em;
  height: 2em;
  margin-left: auto;
  color: color-get(--root-rgb-text-2);
  pointer-events: none;
}

.lockIcon {
  width: 2em;
  height: 2em;
  flex-shrink: 0;
}

.disabled {
  opacity: 0.8;

  .hint {
    color: color-get(--root-rgb-text-2);
  }
}
</style>
